import { useEffect, useCallback } from 'react';
import { useAppShellDocument } from '@/lib/app-shell';
import { Outlet, Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  LayoutDashboard,
  BookOpenCheck,
  Users,
  User,
  LogOut,
  Bell,
  FolderOpen,
  MessageCircle,
  ClipboardList,
  Mic,
} from 'lucide-react';
import { cn, CompassLogo } from '@jeraisonne/ui';
import { useAuth } from '@/lib/auth';
import { SPEAKER_PAGE, useCanRecordVoices } from '@/lib/animations/speaker';
import { supabase } from '@/lib/supabase';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { StudentBottomNav } from '@/components/student-bottom-nav';
import { ManagedChildBar } from '@/components/parent/managed-child-bar';
import { NavBadge } from '@/components/nav-badge';
import { AvatarCircle } from '@/components/avatar-circle';
import { useFeedUnreadCount } from '@/lib/news/use-role-news-feed';
import { ConsentWaitingScreen } from '@/components/consent-waiting-screen';
import {
  isConsentBlocked,
  consentParentEmail,
  type ConsentFields,
} from '@/lib/parental-consent-gate';

// Nav complète pour la sidebar desktop (ordre linéaire)
const ALL_NAV = [
  { id: 'dashboard', label: 'Accueil', icon: LayoutDashboard, to: '/student/dashboard' },
  { id: 'news', label: 'Feed', icon: Bell, to: '/student/news' },
  { id: 'revise', label: 'JeRaisonne', icon: BookOpenCheck, to: '/student/revise' },
  { id: 'evaluations', label: 'Notes et évals', icon: ClipboardList, to: '/student/evaluations' },
  { id: 'contacts', label: 'Mon réseau', icon: Users, to: '/student/contacts' },
  { id: 'files', label: 'Mes fichiers', icon: FolderOpen, to: '/student/files' },
  { id: 'discussions', label: 'Discussions', icon: MessageCircle, to: '/student/discussions' },
  { id: 'profile', label: 'Profil', icon: User, to: '/profile' },
] as const;

/** Voix des animations : comptes « speaker » (migration 0678). */
const SPEAKER_NAV = {
  id: 'speaker',
  label: 'Audios animations',
  icon: Mic,
  to: SPEAKER_PAGE,
} as const;

export function StudentLayout() {
  // La page ne défile pas (seul le contenu) : barre du bas stable après le clavier iOS.
  useAppShellDocument();
  const { user, profile, loading, profileLoading } = useAuth();
  const speaker = useCanRecordVoices();
  const navigate = useNavigate();
  const location = useRouterState({ select: (s) => s.location.pathname });
  // Nouveautés du Feed depuis sa dernière ouverture (masqué sur la page Feed).
  const feedUnread = useFeedUnreadCount('student');
  const feedBadge = location.startsWith('/student/news') ? 0 : feedUnread;
  const queryClient = useQueryClient();

  // Garde RGPD : un élève < 15 ans sans accord parental `active` reste sur
  // l'écran d'attente (le mail au parent est (r)envoyé depuis cet écran).
  const isStudent = profile?.role === 'student';
  const { data: consent, isLoading: consentLoading } = useQuery({
    queryKey: ['student-consent', user?.id],
    enabled: !!user?.id && isStudent,
    staleTime: 30_000,
    queryFn: async (): Promise<ConsentFields | null> => {
      const { data } = await supabase
        .from('student_profiles')
        .select('parental_consent_required, parental_consent_status, parental_consent_email')
        .eq('id', user!.id)
        .maybeSingle();
      return (data as ConsentFields | null) ?? null;
    },
  });

  const handleSignOut = useCallback(async () => {
    queryClient.clear();
    await supabase.auth.signOut();
    navigate({ to: '/login' });
  }, [navigate, queryClient]);

  useEffect(() => {
    if (loading || profileLoading) return;
    if (!profile) {
      navigate({ to: '/onboarding' });
      return;
    }
    if (profile.role !== 'student') {
      navigate({ to: '/' });
    }
  }, [loading, profileLoading, profile, navigate]);

  if (loading || profileLoading || !profile || profile.role !== 'student') return null;

  // Attend le check de consentement avant d'afficher quoi que ce soit, puis
  // bloque sur l'écran d'attente tant que le parent n'a pas validé.
  if (consentLoading) return null;
  if (isConsentBlocked(consent)) {
    return (
      <ConsentWaitingScreen
        email={consentParentEmail(consent)}
        onRefresh={() => {
          void queryClient.invalidateQueries({ queryKey: ['student-consent', user?.id] });
        }}
      />
    );
  }

  return (
    <div className="flex h-dvh overflow-hidden bg-transparent">
      {/* ── Desktop sidebar ──────────────────────────────────────────────── */}
      <aside className="ocean:bg-[linear-gradient(180deg,#0d2f73_0%,#081d4c_100%)] native:pt-[env(safe-area-inset-top)] native:pb-[env(safe-area-inset-bottom)] hidden w-60 shrink-0 flex-col bg-[linear-gradient(180deg,#1f7cf5_0%,#0a5ad6_100%)] text-white shadow-[4px_0_24px_rgba(5,102,242,0.18)] md:flex">
        {/* Brand */}
        <div className="px-5 pb-6 pt-5">
          <CompassLogo size="md" animated wordmarkClassName="text-white" />
          <p className="mt-1 font-sans text-xs text-white/75">Espace élève</p>
        </div>

        {/* Nav */}
        <nav className="flex flex-col gap-1 px-3">
          {[...ALL_NAV, ...(speaker ? [SPEAKER_NAV] : [])].map(({ id, label, icon: Icon, to }) => {
            const active = location.startsWith(to);
            return (
              <Link
                key={id}
                to={to}
                className={cn(
                  'font-display flex min-h-[2.75rem] items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors',
                  active
                    ? 'text-primary-700 ocean:bg-[rgba(255,255,255,0.16)] ocean:text-[#ffffff] ocean:shadow-none bg-[#ffffff] shadow-[0_4px_12px_rgba(3,40,110,0.18)]'
                    : 'text-white/90 hover:bg-white/10',
                )}
              >
                <Icon
                  className={cn(
                    'h-[18px] w-[18px] shrink-0',
                    active ? 'text-primary-600 ocean:text-[#ffffff]' : 'text-white/80',
                  )}
                />
                {label}
                {id === 'news' && <NavBadge count={feedBadge} />}
              </Link>
            );
          })}
        </nav>

        {/* Profile pill */}
        <div className="mx-3 mb-4 mt-auto rounded-2xl bg-white/10 p-3.5 ring-1 ring-white/15">
          <Link to="/profile" className="flex items-center gap-2.5">
            <AvatarCircle name={profile.display_name} src={profile.avatar_url} size="sm" />
            <span className="font-display min-w-0 truncate text-sm font-bold text-white">
              {profile.display_name ?? 'Élève'}
            </span>
          </Link>
          <button
            onClick={handleSignOut}
            className="mt-3 flex min-h-[2.75rem] w-full items-center gap-2 border-t border-white/15 pt-2 font-sans text-xs text-white/80 transition-colors hover:text-white"
          >
            <LogOut className="h-4 w-4" />
            Se déconnecter
          </button>
        </div>
      </aside>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col">
        <main className="native:pt-[env(safe-area-inset-top)] native:md:pb-[env(safe-area-inset-bottom)]! flex-1 overflow-y-auto pb-20 md:pb-0">
          {/* Compte enfant géré par un parent (0301) : retour au parent avec son code. */}
          <ManagedChildBar />
          <Outlet />
        </main>

        {/* ── Mobile bottom tab bar (5 items, JeRévise centré proéminent) ── */}
        <StudentBottomNav feedBadge={feedBadge} />
      </div>
    </div>
  );
}
